import React, { useState, useEffect } from "react";
import { useNavigate, Navigate, useLocation } from "react-router-dom";
import { getCookie } from "shared/auth/utils/cookieUtils";
import { verifyToken } from "shared/api/client/orvalHelpers";
import { userApi } from "shared/api/client/orval";
import { getApiOrigin } from "shared/api/client/apiOrigin";
import { parseApiError } from "shared/api/client/errorParser";

// Styling
import "../../../styles/legacy/MobileSignup.css";

const MobileSignupPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(false);
  const [accountCreated, setAccountCreated] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  const [isVerified, setIsVerified] = useState<boolean | null>(null);

  const isCreatorSignup = location.hash === "#k9x2m7p4q8r3v6n1w5z0";
  const params = new URLSearchParams(location.search);
  const inviteToken = (params.get("invite") || params.get("token") || "").trim();
  const userType = inviteToken ? "Curator" : isCreatorSignup ? "Creator" : "Viewer";
  const oauthQuery = inviteToken ? `?token=${encodeURIComponent(inviteToken)}` : "";

  const GOOGLE_OAUTH_URL_LI = `${getApiOrigin()}/api/User/google-login/${userType}${oauthQuery}`;
  const FACEBOOK_OAUTH_URL_LI = `${getApiOrigin()}/api/User/facebook-login/${userType}${oauthQuery}`;

  const validateEmail = (email: string): string | null => {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailRegex.test(email) ? null : "Please enter a valid email address";
  };

  const validatePassword = (password: string) => {
    const minLength = password.length >= 8 && password.length <= 20;
    const hasCapital = /[A-Z]/.test(password);
    const hasSpecial = /[!@#$%^&*(),.?":{}|<>]/.test(password);
    const hasNumber = /[0-9]/.test(password);

    return minLength && hasCapital && hasSpecial && hasNumber
      ? null
      : "Your password should have 8–20 characters, 1 uppercase letter, 1 special character, and 1 number";
  };

  useEffect(() => {
    const token = getCookie("userToken");
    if (!token) {
      setIsVerified(false);
      return;
    }
    verifyToken(token).then((result: boolean) => setIsVerified(result));
  }, []);

  if (isVerified === null) {
    return <>Loading...</>;
  }

  if (isVerified) {
    return <Navigate to="/" replace />;
  }

  const handleCreateAccount = async (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.preventDefault();

    setErrorMessage("");

    if (!firstName.trim() || !lastName.trim()) {
      setErrorMessage("Please enter both first and last name");
      setTimeout(() => {
        setErrorMessage("");
      }, 3000);
      return;
    }

    const emailError = validateEmail(email);
    if (emailError) {
      setErrorMessage(emailError);
      setTimeout(() => {
        setErrorMessage("");
      }, 3000);
      return;
    }

    const passwordError = validatePassword(password);
    if (passwordError) {
      setErrorMessage(passwordError);
      setTimeout(() => {
        setErrorMessage("");
      }, 3000);
      return;
    }

    setIsLoading(true);

    try {
      // The backend role for viewers is "USER" ("Viewer" is only an OAuth label).
      const role = inviteToken ? "CURATOR" : isCreatorSignup ? "CREATOR" : "USER";
      const payload = {
        email,
        password,
        firstName,
        lastName,
        role,
      };
      if (inviteToken) {
        await userApi.postApiUserSignup(payload, { token: inviteToken });
      } else {
        await userApi.postApiUserCreateusrRole(role, payload);
      }
      setAccountCreated(true);
    } catch (err) {
      console.error("Request error:", err);
      setErrorMessage(parseApiError(err, "Could not create your account."));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="mobile-signup">
      {errorMessage && (
        <div className="error-banner-mobile">
          <img
            src="/images/icons/triangle-warning.svg"
            alt="warning"
            className="warning-mobile"
          />
          <div className="error-banner-text-mobile">{errorMessage}</div>
        </div>
      )}
      <img className="logo" src="/images/poetics-stream-lockup.svg" alt="Poetics Stream logo" />
      {accountCreated ? (
        <div className="success-message-mobile">
          <img
            src="/images/signup/check.png"
            alt="Success"
            className="checkmark-svg"
          />
          <h1 className="success-title-mobile">Done!</h1>
          <p className="success-text">
            Check your inbox for the <br /> verification email
          </p>
          <button
            className="signup-login-button"
            onClick={() => navigate("/login")}
          >
            Enter Poetics Stream
          </button>
        </div>
      ) : (
        <>
          <div className="top-message-mobile">
            <div className="title-login-mobile">Create your account</div>
            <div className="subtitle-login-mobile">
              Sign up to manage your profile and pair your <br />
              devices
            </div>
          </div>
          <div className="mid-container-login-mobile">
            <div className="mid-container-text-mobile">Sign up with</div>
            <div className="buttons-login-mobile">
              <button
                className="sso-button-mobile"
                onClick={() => (window.location.href = GOOGLE_OAUTH_URL_LI)}
              >
                <div className="sso-button-logo-text-mobile">
                  <img
                    src="/images/mobile/SSO_buttons/google.svg"
                    alt="Google"
                    className="sso-logo-mobile"
                  />
                  Google
                </div>
              </button>
              <button
                className="sso-button-mobile"
                onClick={() => (window.location.href = FACEBOOK_OAUTH_URL_LI)}
              >
                <div className="sso-button-logo-text-mobile">
                  <svg
                    viewBox="0 0 320 512"
                    className="sso-logo-mobile"
                    aria-hidden="true"
                    focusable="false"
                  >
                    <path
                      fill="currentColor"
                      d="M279.14 288l14.22-92.66h-88.91V117.13c0-25.35 12.42-50.06 52.24-50.06h40.42V6.26S260.43 0 225.36 0c-73.22 0-121.08 44.38-121.08 124.72v70.62H22.89V288h81.39v224h100.17V288z"
                    />
                  </svg>
                  Facebook
                </div>
              </button>
            </div>
            <div className="mid-container-text-mobile">or</div>
            {isLoading ? (
              <div className="spinner-container-mobile">
                <span className="spinner-mobile" />
              </div>
            ) : (
              <div className="form-container-mobile">
                <div className="name-fields-mobile">
                  <input
                    type="text"
                    placeholder="First name"
                    className="input-field-login-mobile"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                  />
                  <input
                    type="text"
                    placeholder="Last name"
                    className="input-field-login-mobile"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                  />
                </div>
                <input
                  type="email"
                  placeholder="Email"
                  className="input-field-login-mobile"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
                <input
                  type="password"
                  placeholder="Password"
                  className="input-field-login-mobile"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />

                <button
                  className="login-button"
                  onClick={handleCreateAccount}
                  disabled={isLoading}
                >
                  Create account
                </button>
                <p className="create-account-mobile">
                  Already have an account? <a href="/mobile/login">Log in</a>
                </p>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default MobileSignupPage;
